import {
    TARIFF_GATE_STATES as STATES,
    TARIFF_GATE_ACTIONS as ACTIONS,
    TARIFF_GATE_REASON_CODES as REASONS,
    getDowngradePolicy,
} from '@/constants/tariffFeatureKeys'
import { getFeatureCapability } from '@/services/tariffs/tariffAccessResolver'
import { getFeatureUsage } from '@/services/tariffs/tariffUsageResolver'
import { toSafeNumber } from '@/services/tariffs/tariffValueNormalizer'

function buildResult(overrides) {
    return {
        allowed: false,
        state: STATES.UNKNOWN,
        featureKey: null,
        action: null,
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        isGrandfathered: false,
        isUnlimited: false,
        used: null,
        limit: null,
        remaining: null,
        reasonCode: REASONS.TARIFF_FEATURE_UNKNOWN,
        requiredValue: null,
        ...overrides,
    }
}

function pickAllowedForAction(action, flags) {
    switch (action) {
        case ACTIONS.READ:
            return flags.canRead
        case ACTIONS.CREATE:
        case ACTIONS.COPY:
            return flags.canCreate
        case ACTIONS.UPDATE:
        case ACTIONS.PUBLISH:
            return flags.canUpdate
        case ACTIONS.DELETE:
            return flags.canDelete
        default:
            return false
    }
}

export function resolveFeatureGate(params = {}) {
    const {
        featureKey,
        action = ACTIONS.READ,
        accessMap = null,
        usageMap = null,
        hasExistingData = false,
        requiredValue = 1,
        isLoading = false,
        collaborationPermissions = null,
        requiredCollaborationPermission = null,
        deletable = true,
    } = params

    const base = { featureKey, action, requiredValue }

    if (isLoading || !(accessMap instanceof Map)) {
        const loadingFlags = { canRead: hasExistingData, canCreate: false, canUpdate: false, canDelete: false }
        return buildResult({
            ...base,
            ...loadingFlags,
            allowed: pickAllowedForAction(action, loadingFlags),
            state: STATES.LOADING,
            reasonCode: REASONS.TARIFF_ACCESS_LOADING,
        })
    }

    const policy = getDowngradePolicy(featureKey)
    const capability = getFeatureCapability(accessMap, featureKey)

    let flags
    let state
    let reasonCode
    let isUnlimited = false
    let usedOut = null
    let limitOut = null
    let remainingOut = null

    if (!capability.isKnown) {
        flags = { canRead: hasExistingData, canCreate: false, canUpdate: false, canDelete: hasExistingData && deletable }
        state = STATES.UNKNOWN
        reasonCode = REASONS.TARIFF_FEATURE_UNKNOWN
    } else if (capability.isAvailable) {
        isUnlimited = capability.isUnlimited

        if (policy !== 'quantitative' || isUnlimited) {
            flags = { canRead: true, canCreate: true, canUpdate: true, canDelete: true }
            state = STATES.ALLOWED
            reasonCode = REASONS.ALLOWED
            limitOut = isUnlimited ? null : toSafeNumber(capability.rawValue)
        } else if (!(usageMap instanceof Map)) {
            flags = { canRead: true, canCreate: false, canUpdate: true, canDelete: true }
            state = STATES.LOADING
            reasonCode = REASONS.TARIFF_ACCESS_LOADING
        } else {
            const usage = getFeatureUsage(usageMap, featureKey)
            usedOut = toSafeNumber(usage.rawUsed)
            limitOut = toSafeNumber(usage.rawLimit)

            if (!usage.isKnown) {
                flags = { canRead: true, canCreate: false, canUpdate: true, canDelete: true }
                state = STATES.USAGE_UNAVAILABLE
                reasonCode = REASONS.TARIFF_FEATURE_USAGE_UNAVAILABLE
            } else {
                const remaining = toSafeNumber(usage.rawRemaining)
                remainingOut = remaining === null ? null : Math.max(0, remaining)

                if (usage.isUnlimited || (remaining !== null && remaining >= requiredValue)) {
                    flags = { canRead: true, canCreate: true, canUpdate: true, canDelete: true }
                    state = STATES.ALLOWED
                    reasonCode = REASONS.ALLOWED
                    isUnlimited = usage.isUnlimited || isUnlimited
                } else if (remaining === null) {
                    flags = { canRead: true, canCreate: false, canUpdate: true, canDelete: true }
                    state = STATES.USAGE_UNAVAILABLE
                    reasonCode = REASONS.TARIFF_FEATURE_USAGE_UNAVAILABLE
                } else {
                    flags = { canRead: true, canCreate: false, canUpdate: true, canDelete: true }
                    state = STATES.QUOTA_EXHAUSTED
                    reasonCode = REASONS.TARIFF_FEATURE_QUOTA_EXHAUSTED
                }
            }
        }
    } else if (hasExistingData) {
        if (!deletable) {
            flags = { canRead: true, canCreate: false, canUpdate: false, canDelete: false }
            state = STATES.READ_ONLY
            reasonCode = REASONS.TARIFF_FEATURE_UNAVAILABLE
        } else if (policy === 'grandfathered') {
            flags = { canRead: true, canCreate: false, canUpdate: false, canDelete: true }
            state = STATES.GRANDFATHERED
            reasonCode = REASONS.TARIFF_FEATURE_GRANDFATHERED
        } else {
            flags = { canRead: true, canCreate: false, canUpdate: false, canDelete: true }
            state = STATES.DELETE_ONLY
            reasonCode = REASONS.TARIFF_FEATURE_UNAVAILABLE
        }
    } else {
        flags = { canRead: false, canCreate: false, canUpdate: false, canDelete: false }
        state = STATES.UNAVAILABLE
        reasonCode = REASONS.TARIFF_FEATURE_UNAVAILABLE
    }

    const isGrandfathered = state === STATES.GRANDFATHERED

    if (action !== ACTIONS.READ && requiredCollaborationPermission && collaborationPermissions) {
        const collaborationAllowed = Boolean(collaborationPermissions[requiredCollaborationPermission])

        if (!collaborationAllowed) {
            return buildResult({
                ...base,
                ...flags,
                allowed: false,
                state: STATES.COLLABORATION_FORBIDDEN,
                reasonCode: REASONS.COLLABORATION_PERMISSION_DENIED,
                isGrandfathered,
                isUnlimited,
                used: usedOut,
                limit: limitOut,
                remaining: remainingOut,
            })
        }
    }

    return buildResult({
        ...base,
        ...flags,
        allowed: pickAllowedForAction(action, flags),
        state,
        reasonCode,
        isGrandfathered,
        isUnlimited,
        used: usedOut,
        limit: limitOut,
        remaining: remainingOut,
    })
}
